import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  TableContainer,
  CssBaseline,
  Container,
  Dialog,
  Chip,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Card,
  Stack,
  useTheme,
  useMediaQuery,
  TextField,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { Add, Delete, Visibility, ArrowBack } from '@mui/icons-material';
import Header from '../Includes/Header';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';
import AppToast from '../../components/Toast/AppToast';

// Utility function for formatting time
const formatTo12Hour = (time) => {
  if (!time) return '';
  const [hour, minute] = time.split(':');
  const date = new Date();
  date.setHours(parseInt(hour), parseInt(minute));
  return date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

// Mobile Card Component
const MobileCard = ({ diet, onViewDays, onAssign, onDelete }) => {
  const { colors: appColors } = useAppTheme();
  return (
  <Card
    sx={{ 
      p: 2, 
      mb: 2, 
      borderRadius: 2,
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      '&:hover': { boxShadow: '0 4px 8px rgba(0,0,0,0.12)' }
    }}
  >
    <Typography variant="subtitle1" sx={{ color: appColors.evergreen, fontWeight: 600, mb: 1 }}>
      {diet.diet_name}
    </Typography>
    
    {/* Time slots */}
    {[
      ['Pre-Workout', diet.preworkout, diet.preworkout_time],
      ['Post-Workout', diet.post_workout, diet.post_workout_time],
      ['Breakfast', diet.breakfast, diet.breakfast_time],
      ['Morning Snacks', diet.morning_snaks, diet.morning_snaks_time],
      ['Evening Snacks 1', diet.evening_snaks1, diet.evening_snaks1_time],
      ['Evening Snacks 2', diet.evening_snaks2, diet.evening_snaks2_time],
      ['Dinner', diet.dinner, diet.dinner_time],
      ['Meal 1', diet.meal1, diet.meal1_time],
      ['Meal 2', diet.meal2, diet.meal2_time],
    ].map(([label, meal, time], index) => meal && (
      <Box key={index} sx={{ mb: 1 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
          {label}
        </Typography>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="body2">{meal}</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {formatTo12Hour(time)}
          </Typography>
        </Stack>
      </Box>
    ))}

    {/* Actions */}
    <Stack 
      direction="row" 
      spacing={1} 
      sx={{ 
        mt: 2,
        pt: 2,
        borderTop: '1px solid #e5e7eb',
        justifyContent: 'flex-end'
      }}
    >
      <IconButton
        size="small"
        onClick={() => onViewDays(diet.id)}
        sx={{
          color: appColors.evergreen,
          bgcolor: 'rgba(31, 122, 84, 0.05)',
          '&:hover': { 
            bgcolor: appColors.evergreen,
            color: 'white',
            boxShadow: '0 4px 12px rgba(31, 122, 84, 0.15)'
          },
        }}
      >
        <Visibility fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => onAssign(diet.id)}
        sx={{
          color: '#047857',
          bgcolor: 'rgba(4, 120, 87, 0.05)',
          '&:hover': { 
            bgcolor: '#047857',
            color: 'white',
            boxShadow: '0 4px 12px rgba(4, 120, 87, 0.15)'
          },
        }}
      >
        <Add fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => onDelete(diet.id)}
        sx={{
          color: '#dc2626',
          bgcolor: 'rgba(220, 38, 38, 0.05)',
          '&:hover': { 
            bgcolor: '#dc2626',
            color: 'white',
            boxShadow: '0 4px 12px rgba(220, 38, 38, 0.15)'
          },
        }}
      >
        <Delete fontSize="small" />
      </IconButton>
    </Stack>
  </Card>
  );
};

const DietChatPage = () => {
  const { colors: appColors } = useAppTheme();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [dietPlans, setDietPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDaysId, setOpenDaysId] = useState(null);
  const [openAssignId, setOpenAssignId] = useState(null);
  const [selectedUser, setSelectedUser] = useState('');
  const [page] = useState(1);
  const [limit] = useState(10);
  const [deleteDietPlanId, setDeleteDietPlanId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [assignFile, setAssignFile] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [mockUsers, setMockUsers] = useState([]);
  
  useEffect(() => {
    fetchDietPlans();
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    const userData = JSON.parse(localStorage.getItem('userdata'));
    const userToken = userData?.token;
  
    if (!userToken) {
      console.error('No token found');
      return;
    }
  
    try {
      const response = await fetch(`${window.constants.api_url}/members`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userToken}`,
        },
      });
  
      const data = await response.json();
  
      if (data.code === 200) {
        setMockUsers(data.data);
      } else {
        console.error('Failed to fetch members:', data.message);
      }
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  };
  
  const handleDeleteDietPlan = async () => {
    if (!deleteDietPlanId) return;
    const userData = JSON.parse(localStorage.getItem('userdata'));
    const userToken = userData?.token;
  
    setDeleting(true);
    try {
      const response = await fetch(`${window.constants.api_url}/diet-plan/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userToken}`,
        },
        body: JSON.stringify({ id: deleteDietPlanId }),
      });
  
      const result = await response.json();
      if (result.code === 200) {
        fetchDietPlans(); // Refresh list
      } else {
        console.error('Delete failed:', result?.message);
      }
    } catch (error) {
      console.error('Error deleting diet plan:', error);
    } finally {
      setDeleting(false);
      setDeleteDietPlanId(null);
    }
  };
  
  const fetchDietPlans = async () => {
    setLoading(true);
    const userData = JSON.parse(localStorage.getItem('userdata'));
    const userToken = userData?.token;
    try {
      const response = await fetch(`${window.constants.api_url}/diet-plans`,{
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userToken}`,
        },
        body: JSON.stringify({ page, limit }),
      });
      const data = await response.json();
      setDietPlans(data.data || []);
    } catch (error) {
      console.error('Error fetching diet plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDays = (id) => setOpenDaysId(id);
  const handleCloseDays = () => setOpenDaysId(null);
  const handleOpenAssign = (id) => setOpenAssignId(id);
  const handleCloseAssign = () => {
    setSelectedUsers([]);
    setAssignFile(null);
    setOpenAssignId(null);
  };
  const handleSave = async (dietId, selectedUsers) => {
    if (!selectedUsers || selectedUsers.length === 0) {
      console.error('Please select at least one user');
      return;
    }
    const userData = JSON.parse(localStorage.getItem('userdata'));
    const userToken = userData?.token;
    try {
      let response;
      if (assignFile) {
        const fd = new FormData();
        fd.append('diet_id', dietId);
        selectedUsers.forEach((id) => fd.append('member_id[]', id));
        fd.append('attachment', assignFile);
        response = await fetch(`${window.constants.api_url}/assign-member-to-diet`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${userToken}` },
          body: fd,
        });
      } else {
        response = await fetch(`${window.constants.api_url}/assign-member-to-diet`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${userToken}` },
          body: JSON.stringify({ diet_id: dietId, member_id: selectedUsers }),
        });
      }
      const data = await response.json();
      if (data.code === 200) {
        setSelectedUsers([]);
        setAssignFile(null);
        setOpenAssignId(null);
        setToast({ open: true, message: data.message || 'Members assigned successfully.', severity: 'success' });
        navigate('/diet-assign-user');
      } else {
        setToast({ open: true, message: data.message || 'Assignment failed.', severity: 'error' });
      }
    } catch (error) {
      console.error('Error assigning members:', error);
      setToast({ open: true, message: 'An error occurred. Please try again.', severity: 'error' });
    }
  };
  
  
  const handleSaveClick = (dietId) => {
    handleSave(dietId, selectedUsers);
  };
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f7fa' }}>
        <CssBaseline />
        <Header />
      <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, sm: 3 }, mt: 8 }}>
          <Container maxWidth="xl">
          {/* Sub-nav: Diet Plans | Assign Diet User */}
          <Card sx={{ p: 1.5, mb: 2, borderRadius: 2, boxShadow: '0 2px 4px rgba(0,0,0,0.06)' }}>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                size="small"
                variant="contained"
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                Diet Plans
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => navigate('/diet-assign-user')}
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  color: appColors.evergreen,
                  borderColor: appColors.evergreen,
                  '&:hover': { borderColor: appColors.pine, bgcolor: 'rgba(31, 122, 84, 0.04)' },
                }}
              >
                Assign Diet User
              </Button>
            </Stack>
          </Card>
          {/* Header Section */}
          <Card sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 2, boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              alignItems={{ xs: 'stretch', sm: 'center' }} 
              justifyContent="space-between"
              spacing={2}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <IconButton
                  onClick={() => navigate(-1)}
                  sx={{
                    bgcolor: appColors.evergreen,
                    color: 'white',
                    '&:hover': { bgcolor: appColors.pine },
                  }}
                >
                  <ArrowBack />
                </IconButton>
                <Box>
                  <Typography variant="h5" sx={{ color: appColors.evergreen, fontWeight: 600 }}>
                    Diet Plans
                  </Typography>
                  <Breadcrumbs sx={{ mt: 0.5 }}>
                    <Link underline="hover" color="inherit" href="#">
                      Home
                    </Link>
                    <Typography color="text.primary">Diet Plans</Typography>
            </Breadcrumbs>
            </Box>
              </Stack>
              <Button
                fullWidth={isMobile}
                variant="contained"
                onClick={() => navigate('/create-chart')}
                sx={{
                  bgcolor: appColors.evergreen,
                  '&:hover': { bgcolor: appColors.pine },
                  textTransform: 'none',
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  fontWeight: 600,
                }}
              >
                Create Diet Plan
              </Button>
            </Stack>
          </Card>

          {/* Content Section */}
            {loading ? (
              <Box display="flex" justifyContent="center" py={10}>
              <CircularProgress sx={{ color: appColors.evergreen }} />
            </Box>
          ) : (
            <>
              {/* Mobile View */}
              {isMobile ? (
                <Box>
                  {dietPlans.map((diet) => (
                    <MobileCard
                      key={diet.id}
                      diet={diet}
                      onViewDays={handleOpenDays}
                      onAssign={handleOpenAssign}
                      onDelete={setDeleteDietPlanId}
                    />
                  ))}
              </Box>
            ) : (
                /* Desktop Table View - Keep existing table code */
                <Card sx={{ borderRadius: 2, boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: appColors.evergreen }}>
                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Diet</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Pre-WO</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Post-WO</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>BF</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>M-Snack</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>E-Snack 1</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>E-Snack 2</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Dinner</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Meal 1</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Meal 2</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                        {dietPlans.map((diet) => (
                          <TableRow
                            key={diet.id}
                            sx={{
                              '&:nth-of-type(odd)': { bgcolor: '#f8fafc' },
                              '&:hover': { bgcolor: '#f1f5f9' },
                            }}
                          >
  <TableCell>{diet.diet_name}</TableCell>
  <TableCell>
                              <Typography variant="body2">{diet.preworkout}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatTo12Hour(diet.preworkout_time)}
                              </Typography>
  </TableCell>
  <TableCell>
                              <Typography variant="body2">{diet.post_workout}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatTo12Hour(diet.post_workout_time)}
                              </Typography>
  </TableCell>
  <TableCell>
                              <Typography variant="body2">{diet.breakfast}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatTo12Hour(diet.breakfast_time)}
                              </Typography>
  </TableCell>
  <TableCell>
                              <Typography variant="body2">{diet.morning_snaks}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatTo12Hour(diet.morning_snaks_time)}
                              </Typography>
  </TableCell>
  <TableCell>
                              <Typography variant="body2">{diet.evening_snaks1}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatTo12Hour(diet.evening_snaks1_time)}
                              </Typography>
  </TableCell>
  <TableCell>
                              <Typography variant="body2">{diet.evening_snaks2}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatTo12Hour(diet.evening_snaks2_time)}
                              </Typography>
  </TableCell>
  <TableCell>
                              <Typography variant="body2">{diet.dinner}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatTo12Hour(diet.dinner_time)}
                              </Typography>
  </TableCell>
  <TableCell>
                              <Typography variant="body2">{diet.meal1}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatTo12Hour(diet.meal1_time)}
                              </Typography>
  </TableCell>
  <TableCell>
                              <Typography variant="body2">{diet.meal2}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatTo12Hour(diet.meal2_time)}
                              </Typography>
  </TableCell>
                        <TableCell>
                              <Stack 
                                direction="row" 
                                spacing={1}
                                sx={{
                                  background: 'linear-gradient(145deg, #ffffff, #f3f4f6)',
                                  borderRadius: '12px',
                                  padding: '4px',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                                  border: '1px solid #e5e7eb',
                                  '.MuiIconButton-root': {
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                      transform: 'translateY(-2px)',
                                    }
                                  }
                                }}
                              >
                          <IconButton
                                  size="small"
                            onClick={() => handleOpenDays(diet.id)}
                                  sx={{
                                    color: appColors.evergreen,
                                    bgcolor: 'rgba(31, 122, 84, 0.05)',
                                    '&:hover': { 
                                      bgcolor: appColors.evergreen,
                                      color: 'white',
                                      boxShadow: '0 4px 12px rgba(31, 122, 84, 0.15)'
                                    },
                                  }}
                          >
                            <Visibility />
                          </IconButton>
                            <IconButton
                                  size="small"
                              onClick={() => handleOpenAssign(diet.id)}
                                  sx={{
                                    color: '#047857',
                                    bgcolor: 'rgba(4, 120, 87, 0.05)',
                                    '&:hover': { 
                                      bgcolor: '#047857',
                                      color: 'white',
                                      boxShadow: '0 4px 12px rgba(4, 120, 87, 0.15)'
                                    },
                                  }}
                            >
                              <Add />
                            </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => setDeleteDietPlanId(diet.id)}
                                  sx={{
                                    color: '#dc2626',
                                    bgcolor: 'rgba(220, 38, 38, 0.05)',
                                    '&:hover': { 
                                      bgcolor: '#dc2626',
                                      color: 'white',
                                      boxShadow: '0 4px 12px rgba(220, 38, 38, 0.15)'
                                    },
                                  }}
                                >
                              <Delete />
                            </IconButton>
                              </Stack>
                        </TableCell>

                        {/* Days Dialog */}
                            <Dialog
                              open={openDaysId === diet.id}
                              onClose={handleCloseDays}
                              maxWidth="sm"
                              fullWidth
                              PaperProps={{
                                sx: { borderRadius: 2 }
                              }}
                            >
                              <DialogTitle sx={{ 
                                bgcolor: appColors.evergreen,
                                color: 'white',
                                py: 2
                              }}>
                                Diet Plan Days
                              </DialogTitle>
                              <DialogContent sx={{ mt: 2 }}>
                                <Stack spacing={1}>
                            {(diet.days || []).map((day, idx) => (
                                    <Chip
                                      key={idx}
                                      label={day}
                                      sx={{
                                        bgcolor: '#f1f5f9',
                                        color: appColors.evergreen,
                                        fontWeight: 500
                                      }}
                                    />
                            ))}
                                </Stack>
                          </DialogContent>
                              <DialogActions sx={{ p: 2, gap: 1 }}>
                                <Button
                                  onClick={handleCloseDays}
                                  variant="outlined"
                                  sx={{
                                    color: appColors.evergreen,
                                    borderColor: appColors.evergreen,
                                    '&:hover': {
                                      borderColor: appColors.pine,
                                      bgcolor: 'rgba(31, 122, 84, 0.04)'
                                    },
                                    textTransform: 'none',
                                    fontWeight: 600
                                  }}
                                >
                                  Close
                                </Button>
                          </DialogActions>
                        </Dialog>

                        {/* Assign User Dialog */}
                            <Dialog
                              open={openAssignId === diet.id}
                              onClose={handleCloseAssign}
                              maxWidth="sm"
                              fullWidth
                              PaperProps={{
                                sx: { borderRadius: 2 }
                              }}
                            >
                              <DialogTitle sx={{ 
                                bgcolor: appColors.evergreen,
                                color: 'white',
                                py: 2
                              }}>
                                Assign Members
                              </DialogTitle>
                              <DialogContent sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                                <Autocomplete
                                  multiple
                                  options={mockUsers}
                                  getOptionLabel={(u) => u.membership_info ?? ''}
                                  value={mockUsers.filter((u) => selectedUsers.includes(u.id))}
                                  onChange={(_, newVal) => setSelectedUsers(newVal.map((u) => u.id))}
                                  filterSelectedOptions
                                  renderTags={(selected, getTagProps) =>
                                    selected.map((u, index) => (
                                      <Chip key={u.id} label={u.membership_info} size="small"
                                        sx={{ bgcolor: 'rgba(27,67,50,0.1)', color: appColors.evergreen, fontWeight: 600 }}
                                        {...getTagProps({ index })}
                                      />
                                    ))
                                  }
                                  renderInput={(params) => (
                                    <TextField {...params} label="Search & Select Members" placeholder="Type name or membership no..."
                                      sx={{ '& .MuiOutlinedInput-root': { '&.Mui-focused fieldset': { borderColor: appColors.evergreen } }, '& label.Mui-focused': { color: appColors.evergreen } }}
                                    />
                                  )}
                                  ListboxProps={{ style: { maxHeight: 260 } }}
                                />

                                {/* File Attachment */}
                                <Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                    Attachment (PDF or Excel) — optional
                                  </Typography>
                                  <Button
                                    component="label"
                                    variant="outlined"
                                    size="small"
                                    sx={{ textTransform: 'none', color: appColors.evergreen, borderColor: appColors.evergreen, '&:hover': { borderColor: appColors.pine, bgcolor: 'rgba(27,67,50,0.04)' } }}
                                  >
                                    📎 {assignFile ? 'Change file' : 'Upload file'}
                                    <input
                                      type="file"
                                      hidden
                                      accept=".pdf,.xls,.xlsx,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                      onChange={(e) => setAssignFile(e.target.files[0] || null)}
                                    />
                                  </Button>
                                  {assignFile && (
                                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Chip
                                        label={assignFile.name}
                                        size="small"
                                        onDelete={() => setAssignFile(null)}
                                        sx={{ bgcolor: appColors.leafSoft, color: appColors.evergreen, maxWidth: 320 }}
                                      />
                                      <Typography variant="caption" color="text.secondary">
                                        {(assignFile.size / 1024).toFixed(1)} KB
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              </DialogContent>
                              <DialogActions sx={{ p: 2, gap: 1 }}>
                                <Button
                                  onClick={handleCloseAssign}
                                  variant="outlined"
                                  sx={{
                                    color: appColors.evergreen,
                                    borderColor: appColors.evergreen,
                                    '&:hover': {
                                      borderColor: appColors.pine,
                                      bgcolor: 'rgba(31, 122, 84, 0.04)'
                                    },
                                    textTransform: 'none',
                                    fontWeight: 600
                                  }}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  onClick={() => handleSaveClick(diet.id)}
                                  variant="contained"
                                  sx={{
                                    bgcolor: appColors.evergreen,
                                    '&:hover': { bgcolor: appColors.pine },
                                    textTransform: 'none',
                                    fontWeight: 600
                                  }}
                                >
                                  Save
                                </Button>
      </DialogActions>
    </Dialog>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                  </TableContainer>
                </Card>
              )}
            </>
            )}
          </Container>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={Boolean(deleteDietPlanId)}
        onClose={() => setDeleteDietPlanId(null)}
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: appColors.evergreen,
          color: 'white',
          py: 2
        }}>
          Delete Diet Plan
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
    <Typography>Are you sure you want to delete this diet plan?</Typography>
  </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setDeleteDietPlanId(null)}
            variant="outlined"
            disabled={deleting}
            sx={{
              color: appColors.evergreen,
              borderColor: appColors.evergreen,
              '&:hover': {
                borderColor: appColors.pine,
                bgcolor: 'rgba(31, 122, 84, 0.04)'
              },
              textTransform: 'none',
              fontWeight: 600
            }}
          >
      Cancel
    </Button>
    <Button
      onClick={handleDeleteDietPlan}
      variant="contained"
      disabled={deleting}
            sx={{
              bgcolor: '#dc2626',
              '&:hover': { bgcolor: '#b91c1c' },
              textTransform: 'none',
              fontWeight: 600
            }}
    >
      {deleting ? <CircularProgress size={24} /> : 'Delete'}
    </Button>
  </DialogActions>
</Dialog>
      <AppToast open={toast.open} message={toast.message} severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))} />
    </Box>
  );
};

export default DietChatPage;
